import { v4 as uuidv4 } from 'uuid';

import { getPlainText } from '@gem/modules/markdown';

import { THighlightedTextPosition, THighlightLayout } from './TextHighlighter.types';

export const TEXT_HIGHLIGHT_BORDER_RADIUS = 2;

function getHighlightedTextRange(
  position: THighlightedTextPosition,
  htmlElement: HTMLElement
): Range | null {
  const range = document.createRange();
  const treeWalker = document.createTreeWalker(htmlElement, NodeFilter.SHOW_TEXT, null);

  let startNode: Node | null = null;
  let endNode: Node | null = null;
  let node: Node | null;
  let startOffset = 0;
  let endOffset = 0;
  let pos = 0;

  while ((node = treeWalker.nextNode())) {
    const nodeLength = node?.nodeValue?.length || 0;

    if (!startNode && pos + nodeLength > position.start) {
      startNode = node;
      startOffset = position.start - pos;
    }

    if (!endNode && pos + nodeLength >= position.end) {
      endNode = node;
      endOffset = position.end - pos;
      break;
    }

    pos += nodeLength;
  }

  if (startNode && endNode) {
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
  }

  return range;
}

function getSortedRangeRects(range: Range | null): DOMRect[] {
  if (!range) {
    return [];
  }

  const highlightRects: DOMRect[] = Array.from(range.getClientRects());
  return highlightRects.sort((a, b) => (a.top === b.top ? a.left - b.left : a.top - b.top));
}

function getHighlightLayout(
  rangeRects: DOMRect[],
  parentTop: number,
  parentLeft: number,
  rangeHeight: number
): THighlightLayout[] {
  return rangeRects.reduce<THighlightLayout[]>((acc, rect, i) => {
    const prevParams = acc[acc.length - 1];
    const currentLeft = rect.left - parentLeft;
    const currentTop = rect.top - parentTop;

    const currentParams: THighlightLayout = {
      left: currentLeft,
      right: currentLeft + rect.width,
      top: currentTop,
      bottom: currentTop + Math.max(rangeHeight, rect.height),
      id: uuidv4(),
      touches: [],
    };

    if (!prevParams) {
      acc.push(currentParams);
      return acc;
    }

    if (prevParams.top === currentParams.top && currentParams.left <= prevParams.right) {
      prevParams.right = Math.max(currentParams.right, prevParams.right);
      return acc;
    }

    acc.push(currentParams);
    return acc;
  }, []);
}

function getHighlightParamsWithTouches(params: THighlightLayout[]): THighlightLayout[] {
  return params.reduce<THighlightLayout[]>((acc, currentParams, i) => {
    const prevParams = acc[acc.length - 1];

    if (!prevParams) {
      acc.push(currentParams);
      return acc;
    }

    // Красивые соединения соседних строк, если пересекаются
    if (Math.abs(currentParams.left - prevParams.left) < TEXT_HIGHLIGHT_BORDER_RADIUS) {
      prevParams.touches.push('left-bottom-straight');
      currentParams.touches.push('left-top-straight');
    } else if (prevParams.left <= currentParams.right) {
      prevParams.touches.push('left-bottom');
    }

    if (Math.abs(currentParams.right - prevParams.right) < TEXT_HIGHLIGHT_BORDER_RADIUS) {
      currentParams.touches.push('right-top-straight');
      prevParams.touches.push('right-bottom-straight');
    } else if (prevParams.left <= currentParams.right && prevParams.right >= currentParams.right) {
      currentParams.touches.push('right-top');
    }

    if (prevParams.right > currentParams.left && prevParams.right < currentParams.right) {
      prevParams.touches.push('right-bottom');
    }

    if (prevParams.right > currentParams.left && prevParams.left < currentParams.left) {
      currentParams.touches.push('left-top');
    }

    acc.push(currentParams);
    return acc;
  }, []);
}

function getHighlightedTextPosition(
  searchText: string,
  originalText: string
): THighlightedTextPosition | null {
  const plainText = getPlainText(originalText);
  const searchRegex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  const start = searchRegex.exec(plainText)?.index;

  if (start === undefined) {
    return null;
  }

  return {
    start,
    end: start + searchText.length,
  };
}

export function getHighlightParams(
  searchText?: string | null,
  originalText?: string,
  htmlElement?: HTMLElement | null
): { highlightLayout: THighlightLayout[]; relativeTop: number } | null {
  if (!searchText || !originalText || !htmlElement) {
    return null;
  }

  const highlightedTextPosition = getHighlightedTextPosition(searchText, originalText);
  if (!highlightedTextPosition) {
    return null;
  }

  const range = getHighlightedTextRange(highlightedTextPosition, htmlElement);

  const rangeRects = getSortedRangeRects(range);
  const parentRect = htmlElement.getBoundingClientRect();
  const parentTop = rangeRects[0]?.top ?? 0;
  const relativeTop = parentTop - parentRect.top;
  const parentLeft = parentRect.left;
  const rangeHeight = parseInt(window.getComputedStyle(htmlElement).lineHeight, 10);

  const highlightLayout =
    (rangeRects.length && getHighlightLayout(rangeRects, parentTop, parentLeft, rangeHeight)) || [];

  return {
    highlightLayout: getHighlightParamsWithTouches(highlightLayout),
    relativeTop,
  };
}
